<template>
  <div class="check-in-page">
    <div class="page-content">
      <!-- Header -->
      <div class="page-header">
        <button class="back-btn" @click="goBack">
          <feather-icon icon="ChevronLeftIcon" size="20" />
        </button>
        <h1 class="page-title">เช็คอินรับโบนัส</h1>
        <button class="info-btn" @click="showInfoModal = true">
          <feather-icon icon="InfoIcon" size="20" />
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <b-spinner variant="warning" />
        <p class="loading-text">กำลังโหลด...</p>
      </div>

      <!-- Main Content -->
      <div v-else class="main-content">
        <!-- Reward Card -->
        <div class="reward-card" :class="{ completed: isCompleted }">
          <div class="reward-icon">🎁</div>
          <h2 class="reward-title">รับเครดิตฟรี {{ rewardAmount }} บาท</h2>
          <p class="reward-subtitle">ฝากเงิน 300 บาท ติดต่อกัน 3 วัน</p>
          <div class="reward-status">
            <span v-if="!isCompleted" class="status-pending">
              เหลืออีก {{ daysRemaining }} วัน ⏰
            </span>
            <span v-else class="status-ready">พร้อมรับรางวัล! 🎉</span>
          </div>
        </div>

        <!-- Progress Section -->
        <div class="progress-section">
          <label class="section-label">ความคืบหน้า</label>
          <div class="progress-bar-container">
            <div class="progress-bar-bg">
              <div
                class="progress-bar-fill"
                :style="{ width: progressPercent + '%' }"
              ></div>
              <div class="progress-bar-glow"></div>
            </div>
            <div class="progress-info">
              <span class="progress-percent"
                >{{ Math.round(progressPercent) }}%</span
              >
              <span class="progress-days">{{ currentStreak }}/3 วัน</span>
            </div>
          </div>
        </div>

        <!-- 3-Day Cards Grid -->
        <div class="day-cards-section">
          <div class="day-cards-grid">
            <check-in-card
              v-for="day in 3"
              :key="day"
              :day="day"
              :status="getCardStatus(day)"
              :amount="getCardAmount(day)"
            />
          </div>
        </div>

        <!-- Claim Button -->
        <b-button
          block
          size="lg"
          :variant="canClaim ? 'success' : 'secondary'"
          :disabled="!canClaim"
          class="claim-btn"
          :class="{ active: canClaim, claiming: claiming }"
          @click="handleClaim"
        >
          <b-spinner v-if="claiming" small class="mr-2" />
          <span v-if="!claiming">รับรางวัล {{ rewardAmount }} บาท</span>
          <span v-else>กำลังดำเนินการ...</span>
        </b-button>

        <!-- Rules Section -->
        <div class="rules-section">
          <div class="rules-header" @click="rulesExpanded = !rulesExpanded">
            <span class="rules-icon">📜</span>
            <span class="rules-title">เงื่อนไข</span>
            <span class="rules-arrow">{{ rulesExpanded ? '▲' : '▼' }}</span>
          </div>
          <transition name="slide">
            <div v-if="rulesExpanded" class="rules-content">
              <ul class="rules-list">
                <li>ฝากเงินจำนวน 300 บาทต่อวัน ไม่รับโบนัส</li>
                <li>ต้องฝากติดต่อกัน 3 วันจึงจะได้รับรางวัล</li>
                <li>หากขาดวันจะเริ่มนับใหม่</li>
                <li>กดรับรางวัลเองหลังครบเงื่อนไข</li>
                <li>รางวัลต้องทำเทิร์น 6เท่า</li>
                <li>ถอนสูงสุด 3เท่า</li>
              </ul>
            </div>
          </transition>
        </div>

        <!-- History Section -->
        <div v-if="checkInHistory.length > 0" class="history-section">
          <h3 class="history-title">📊 ประวัติการเช็คอิน</h3>
          <div class="history-list">
            <div
              v-for="(item, index) in checkInHistory.slice(0, 5)"
              :key="index"
              class="history-item"
            >
              <div class="history-date">{{ formatDate(item.date) }}</div>
              <div class="history-status" :class="{ success: item.qualified }">
                {{ item.qualified ? '✓' : '✗' }}
              </div>
              <div class="history-amount">
                ฿ {{ formatNumber(item.amount) }}
              </div>
              <div class="history-result" :class="{ success: item.qualified }">
                {{ item.qualified ? 'เช็คอินสำเร็จ' : 'ไม่ครบเงื่อนไข' }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Claim Modal -->
    <check-in-modal
      v-model="showClaimModal"
      :reward="rewardAmount"
      :success="true"
      @go-dashboard="goToDashboard"
    />

    <!-- Info Modal -->
    <b-modal
      v-model="showInfoModal"
      centered
      title="ข้อมูลระบบเช็คอิน"
      ok-only
      ok-title="เข้าใจแล้ว"
    >
      <p>ระบบเช็คอินจะติดตามการฝากเงินของคุณอัตโนมัติ</p>
      <p>เมื่อฝากเงินครบ 300 บาทในแต่ละวัน จะนับเป็น 1 วันในระบบเช็คอิน</p>
      <p>เมื่อฝากติดต่อกัน 3 วัน คุณสามารถกดรับรางวัล 300 บาทได้ทันที</p>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BButton, BSpinner, BModal } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CheckInCard from '../component/CheckInCard.vue'
import CheckInModal from '../component/CheckInModal.vue'

export default {
  name: 'CheckInPage',
  components: {
    BButton,
    BSpinner,
    BModal,
    CheckInCard,
    CheckInModal
  },
  data() {
    return {
      showClaimModal: false,
      showInfoModal: false,
      rulesExpanded: false
    }
  },
  computed: {
    ...mapState('checkin', [
      'currentStreak',
      'checkInHistory',
      'canClaim',
      'loading',
      'claiming',
      'rewardAmount'
    ]),
    ...mapGetters('checkin', [
      'progressPercent',
      'daysRemaining',
      'isCompleted'
    ])
  },
  methods: {
    ...mapActions('checkin', ['fetchStatus', 'claimReward']),
    ...mapActions('member', ['fetchCredit']),

    goBack() {
      this.$router.go(-1)
    },

    getCardStatus(day) {
      if (day <= this.currentStreak) return 'completed'
      if (day === this.currentStreak + 1) return 'active'
      return 'pending'
    },

    getCardAmount(day) {
      if (this.checkInHistory && this.checkInHistory.length >= day) {
        const historyItem = this.checkInHistory[day - 1]
        return historyItem?.amount || 0
      }
      return 0
    },

    async handleClaim() {
      if (!this.canClaim) return

      try {
        await this.claimReward()

        // Success - show modal in success state and fetch credit
        this.showClaimModal = true
        this.$eventBus.$emit('refreshBalance')
      } catch (error) {
        console.error('Claim error:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text:
              error.response?.data?.message ||
              'ไม่สามารถรับรางวัลได้ กรุณาลองใหม่อีกครั้ง',
            variant: 'danger',
            icon: 'XCircleIcon'
          }
        })
      }
    },

    goToDashboard() {
      this.$router.push({ name: 'dashboard' })
    },

    formatNumber(num) {
      return new Intl.NumberFormat('th-TH').format(num || 0)
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('th-TH', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      })
    }
  },

  created() {
    // Fetch check-in status on page load
    this.fetchStatus()
  }
}
</script>

<style lang="scss" scoped>
.check-in-page {
  width: 100%;
  min-height: 100vh;
  padding: 9px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);

  @media (min-width: 768px) {
    padding: 18px;
  }
}

.page-content {
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
}

// Header
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 11px 0;
}

.back-btn,
.info-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
}

.page-title {
  font-size: 20px;
  font-weight: bold;
  color: #ffcc00;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 24px;
  }
}

// Loading
.loading-container {
  text-align: center;
  padding: 54px 18px;
}

.loading-text {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 14px;
  font-size: 14px;
}

// Main Content
.main-content {
  animation: fadeIn 0.4s ease-out;
}

// Reward Card
.reward-card {
  background: linear-gradient(135deg, #ffcc00, #ff9900);
  border-radius: 14px;
  padding: 22px;
  text-align: center;
  box-shadow: 0 4px 14px rgba(255, 204, 0, 0.3);
  margin-bottom: 18px;
  transition: all 0.3s ease;

  &.completed {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  @media (min-width: 768px) {
    padding: 25px;
  }
}

.reward-icon {
  font-size: 43px;
  margin-bottom: 11px;
  animation: bounce 1s ease infinite;

  @media (min-width: 768px) {
    font-size: 50px;
  }
}

.reward-title {
  font-size: 22px;
  font-weight: bold;
  color: #1a1a2e;
  margin: 0 0 7px 0;

  @media (min-width: 768px) {
    font-size: 25px;
  }
}

.reward-subtitle {
  font-size: 13px;
  color: rgba(26, 26, 46, 0.9);
  margin: 0 0 11px 0;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.reward-status {
  font-size: 12px;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.status-pending {
  color: rgba(26, 26, 46, 0.8);
}

.status-ready {
  color: #28a745;
  font-size: 16px;
  animation: pulse 1s ease-in-out infinite;

  @media (min-width: 768px) {
    font-size: 18px;
  }
}

// Progress Section
.progress-section {
  margin-bottom: 18px;
}

.section-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 11px;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.progress-bar-container {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 11px;
  padding: 14px;
  backdrop-filter: blur(10px);
}

.progress-bar-bg {
  position: relative;
  width: 100%;
  height: 11px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  overflow: hidden;
  margin-bottom: 11px;

  @media (min-width: 768px) {
    height: 14px;
  }
}

.progress-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, #ffcc00, #ff9900);
  border-radius: 8px;
  transition: width 0.5s ease-out;
  box-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
}

.progress-bar-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite linear;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-percent {
  font-size: 18px;
  font-weight: bold;
  color: #ffcc00;

  @media (min-width: 768px) {
    font-size: 20px;
  }
}

.progress-days {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);

  @media (min-width: 768px) {
    font-size: 16px;
  }
}

// Day Cards
.day-cards-section {
  margin-bottom: 18px;
}

.day-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;

  @media (min-width: 768px) {
    gap: 11px;
  }
}

// Claim Button
.claim-btn {
  margin-bottom: 18px;
  font-size: 14px;
  font-weight: bold;
  padding: 13px 22px;
  border-radius: 9px;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    font-size: 16px;
    padding: 14px 29px;
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: rgba(255, 255, 255, 0.4) !important;
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: linear-gradient(135deg, #28a745, #20c997) !important;
    border: none !important;
    animation: pulse-button 2s ease-in-out infinite;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 20px rgba(40, 167, 69, 0.5);
    }
  }
}

// Rules Section
.rules-section {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  margin-bottom: 18px;
  overflow: hidden;
}

.rules-header {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
}

.rules-icon {
  font-size: 18px;
}

.rules-title {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.rules-arrow {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
}

.rules-content {
  padding: 0 14px 14px 14px;
}

.rules-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    font-size: 11px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.7);
    padding: 5px 0;
    padding-left: 18px;
    position: relative;

    @media (min-width: 768px) {
      font-size: 12px;
    }

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #ffcc00;
      font-weight: bold;
    }
  }
}

// History Section
.history-section {
  margin-bottom: 18px;
}

.history-title {
  font-size: 14px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 11px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
}

.history-list {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9px;
  padding: 11px;
  backdrop-filter: blur(10px);
}

.history-item {
  display: grid;
  grid-template-columns: auto 27px auto 1fr;
  gap: 11px;
  align-items: center;
  padding: 9px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 11px;

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    font-size: 12px;
  }
}

.history-date {
  color: rgba(255, 255, 255, 0.7);
}

.history-status {
  font-size: 14px;
  color: #ef4444;

  &.success {
    color: #28a745;
  }
}

.history-amount {
  font-weight: 600;
  color: #ffffff;
}

.history-result {
  text-align: right;
  color: #ef4444;
  font-size: 10px;

  &.success {
    color: #28a745;
  }

  @media (min-width: 768px) {
    font-size: 11px;
  }
}

// Transitions
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 300px;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 4px 16px rgba(255, 204, 0, 0.3);
  }
  50% {
    box-shadow: 0 4px 24px rgba(255, 204, 0, 0.5);
  }
}

@keyframes pulse-button {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(40, 167, 69, 0);
  }
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
</style>
